import { useState, useMemo, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const INR = (v) => "₹" + Math.round(Number(v)||0).toLocaleString("en-IN");
const PCT = (v) => (Math.round((v||0)*10)/10) + "%";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const GST_RATES = [0, 5, 12, 18, 28];
const PLATFORMS = ["shopify","amazon","flipkart","myntra","meesho"];
const PLT_NAMES = { shopify:"Shopify", amazon:"Amazon", flipkart:"Flipkart", myntra:"Myntra", meesho:"Meesho" };

// State code from shipping address
function getStateCode(order) {
  const state = (order.shipping_address?.province || order.ShippingAddress?.StateOrRegion || "").toLowerCase();
  const map = { "uttar pradesh":"UP","maharashtra":"MH","delhi":"DL","karnataka":"KA","tamil nadu":"TN","rajasthan":"RJ","gujarat":"GJ","west bengal":"WB","telangana":"TS","haryana":"HR","madhya pradesh":"MP","punjab":"PB","kerala":"KL","bihar":"BR" };
  return Object.entries(map).find(([k])=>state.includes(k))?.[1] || "OTHER";
}

// GST calculation (seller is in UP, so UP orders = CGST+SGST, others = IGST)
const SELLER_STATE = "UP";
function calcGST(sp, gstRate=18) {
  const outputGST   = sp * gstRate / (100 + gstRate);
  const taxableValue = sp - outputGST;
  return { outputGST, taxableValue };
}

const TABS = [
  { id:"dashboard",  label:"📊 Dashboard"    },
  { id:"gst",        label:"🧾 GST Reports"   },
  { id:"invoices",   label:"📄 Invoices"      },
  { id:"expenses",   label:"💸 Expenses"      },
  { id:"pl",         label:"📈 P&L"           },
  { id:"tally",      label:"📤 Tally Export"  },
  { id:"ai",         label:"🤖 AI Analysis"   },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:  { padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
};

export default function PageFinancePro({ toast, orders=[], products=[], tenant }) {
  const [tab,        setTab]       = useState("dashboard");
  const [month,      setMonth]     = useState(new Date().getMonth());
  const [year,       setYear]      = useState(new Date().getFullYear());
  const [gstRate,    setGstRate]   = useState(18);
  const [platFilter, setPlatFilter]= useState("all");
  const [expenses,   setExpenses]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_expenses")||"[]"); } catch { return []; }
  });
  const [expForm,    setExpForm]   = useState({ date:new Date().toISOString().slice(0,10), category:"", desc:"", amount:"", gstPct:"0" });
  const [showExpForm,setShowExpForm] = useState(false);
  const [editingExp,  setEditingExp]  = useState(null); // expense being edited
  const [aiLoading,  setAiLoading] = useState(false);
  const [aiResult,   setAiResult]  = useState(null);
  const [invoices,   setInvoices]  = useState([]);

  useEffect(() => {
    authFetch("/invoices").then(d => setInvoices(d.invoices||[])).catch(()=>{});
  }, []);

  // Load Myntra orders from localStorage and normalize to same schema
  const myntraOrders = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("3june_myntra_orders") || "[]");
      return raw
        .filter(o => o.status === "C")  // delivered only
        .map(o => ({
          // normalize to same shape as Shopify orders
          id:               o.orderId,
          name:             o.orderId,
          _platform:        "myntra",
          created_at:       o.createdAt || "",
          total_price:      o.sellerPrice || 0,   // use seller price (not GMV) for GST
          fulfillment_status: "fulfilled",
          financial_status: "paid",
          customer: { first_name: o.city || "Myntra", last_name: "Customer" },
          shipping_address: {
            province: o.state || "",
            city:     o.city  || "",
            zip:      o.pin   || "",
          },
          _myntra_gmv:      o.finalAmount,
          _myntra_gt:       o.gtCharges,
          _myntra_sku:      o.sku,
          _myntra_product:  o.productName,
        }));
    } catch { return []; }
  }, []); // reads from localStorage once on mount — refreshes on tab switch

  // Merge Shopify/platform orders + Myntra orders
  const allOrders = useMemo(() => {
    const shopifyEtc = orders.map(o => ({ ...o, _platform: o._platform || "shopify" }));
    return [...shopifyEtc, ...myntraOrders];
  }, [orders, myntraOrders]);

  // Filter by month/year/platform
  const filteredOrders = useMemo(() => {
    return allOrders.filter(o => {
      const d   = new Date(o.created_at || o.PurchaseDate || 0);
      const matchMonth = d.getMonth()===month && d.getFullYear()===year;
      const matchPlat  = platFilter==="all" || o._platform===platFilter;
      return matchMonth && matchPlat;
    });
  }, [allOrders, month, year, platFilter]);

  // GST calculations on filtered orders
  const gstData = useMemo(() => {
    return filteredOrders.map(o => {
      const sp  = parseFloat(o.total_price || o.OrderTotal?.Amount || 0);
      const { outputGST, taxableValue } = calcGST(sp, gstRate);
      const stCode   = getStateCode(o);
      const isIntra  = stCode === SELLER_STATE;
      const cgst     = isIntra ? outputGST/2 : 0;
      const sgst     = isIntra ? outputGST/2 : 0;
      const igst     = isIntra ? 0 : outputGST;
      const platform = o._platform || "shopify";
      const custName = o._platform==="myntra"
        ? (o._myntra_product||"Myntra Order").slice(0,30)
        : o.customer ? (o.customer.first_name||"") + " " + (o.customer.last_name||"") : "Guest";
      const productDesc = o._platform==="myntra" ? o._myntra_product||"" : "";
      return {
        orderId:      o.name || o.AmazonOrderId || o.id,
        date:         new Date(o.created_at||0).toLocaleDateString("en-IN"),
        customer:     custName.trim()||"Guest",
        productDesc,
        platform,
        sp,
        taxableValue: Math.round(taxableValue),
        outputGST:    Math.round(outputGST),
        cgst:         Math.round(cgst),
        sgst:         Math.round(sgst),
        igst:         Math.round(igst),
        stCode,
        gstRate,
      };
    });
  }, [filteredOrders, gstRate]);

  // Totals
  const totals = useMemo(() => ({
    revenue:      gstData.reduce((a,r)=>a+r.sp,0),
    taxable:      gstData.reduce((a,r)=>a+r.taxableValue,0),
    outputGST:    gstData.reduce((a,r)=>a+r.outputGST,0),
    cgst:         gstData.reduce((a,r)=>a+r.cgst,0),
    sgst:         gstData.reduce((a,r)=>a+r.sgst,0),
    igst:         gstData.reduce((a,r)=>a+r.igst,0),
    orders:       gstData.length,
  }), [gstData]);

  // Expense totals
  const expMonthly = useMemo(() => {
    return expenses.filter(e => {
      const d = new Date(e.date||0);
      return d.getMonth()===month && d.getFullYear()===year;
    });
  }, [expenses, month, year]);

  const expTotal     = expMonthly.reduce((a,e)=>a+(parseFloat(e.amount)||0),0);
  const expGSTTotal  = expMonthly.reduce((a,e)=>a+(parseFloat(e.amount)||0)*(parseFloat(e.gstPct)||0)/100,0);
  const expByCategory= {};
  expMonthly.forEach(e => { expByCategory[e.category] = (expByCategory[e.category]||0) + (parseFloat(e.amount)||0); });

  // P&L
  const netRevenue   = totals.taxable;
  const grossProfit  = netRevenue - expTotal;
  const netGSTPayable= totals.outputGST - expGSTTotal;

  // Add expense
  const addExpense = () => {
    if (!expForm.category || !expForm.amount) { toast("Fill category and amount","error"); return; }
    const updated = [{ ...expForm, id:Date.now() }, ...expenses];
    setExpenses(updated);
    localStorage.setItem("3june_expenses", JSON.stringify(updated));
    setExpForm({ date:new Date().toISOString().slice(0,10), category:"", desc:"", amount:"", gstPct:"0" });
    setShowExpForm(false);
    toast("Expense added ✓","success");
  };

  const deleteExpense = (id) => {
    const updated = expenses.filter(e=>e.id!==id);
    setExpenses(updated);
    localStorage.setItem("3june_expenses", JSON.stringify(updated));
  };

  const startEditExpense  = (e) => setEditingExp({ ...e });
  const cancelEditExpense = () => setEditingExp(null);
  const saveEditExpense   = () => {
    if (!editingExp) return;
    const updated = expenses.map(e => e.id===editingExp.id ? { ...editingExp } : e);
    setExpenses(updated);
    localStorage.setItem("3june_expenses", JSON.stringify(updated));
    setEditingExp(null);
    toast("Expense updated ✓", "success");
  };

  // Export GSTR-1
  const exportGSTR1 = () => {
    const headers = ["Order ID","Date","Product/Customer","Platform","Invoice Value","Taxable Value","CGST","SGST","IGST","Total GST","State","GST Rate","Source"];
    const rows    = gstData.map(r=>[r.orderId,r.date,r.productDesc||r.customer,PLT_NAMES[r.platform]||r.platform,r.sp,r.taxableValue,r.cgst,r.sgst,r.igst,r.outputGST,r.stCode,r.gstRate+"%",r.platform==="myntra"?"Myntra CSV Import":"Platform Sync"]);
    const csv     = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a       = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download=`GSTR1_${MONTHS[month]}_${year}.csv`; a.click();
    toast("GSTR-1 exported ✓","success");
  };

  // Export Tally
  const exportTally = () => {
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<ENVELOPE>
  <HEADER><TALLYREQUEST>Import Data</TALLYREQUEST></HEADER>
  <BODY><IMPORTDATA><REQUESTDESC><REPORTNAME>Vouchers</REPORTNAME></REQUESTDESC>
  <REQUESTDATA>
${gstData.map(r=>`    <TALLYMESSAGE><VOUCHER VCHTYPE="Sales">
      <DATE>${r.date.split("/").reverse().join("")}</DATE>
      <NARRATION>${r.orderId} - ${r.customer} - ${PLT_NAMES[r.platform]||r.platform}</NARRATION>
      <AMOUNT>${r.sp}</AMOUNT>
      <CGSTAMOUNT>${r.cgst}</CGSTAMOUNT>
      <SGSTAMOUNT>${r.sgst}</SGSTAMOUNT>
      <IGSTAMOUNT>${r.igst}</IGSTAMOUNT>
    </VOUCHER></TALLYMESSAGE>`).join("\n")}
  </REQUESTDATA></IMPORTDATA></BODY>
</ENVELOPE>`;
    const a = document.createElement("a"); a.href="data:text/xml;charset=utf-8,"+encodeURIComponent(xml); a.download=`Tally_${MONTHS[month]}_${year}.xml`; a.click();
    toast("Tally XML exported ✓","success");
  };

  // AI Analysis
  const runAI = async () => {
    setAiLoading(true);
    try {
      const data = await authFetch("/ai/text", {
        method: "POST",
        body: JSON.stringify({
          prompt: `You are a GST and financial analyst for an Indian ecommerce business.
Analyze this month's financial data and provide insights:

Month: ${MONTHS[month]} ${year}
Revenue: Rs.${Math.round(totals.revenue).toLocaleString("en-IN")}
Taxable Value: Rs.${Math.round(totals.taxable).toLocaleString("en-IN")}
Output GST: Rs.${Math.round(totals.outputGST).toLocaleString("en-IN")}
CGST: Rs.${Math.round(totals.cgst).toLocaleString("en-IN")}
SGST: Rs.${Math.round(totals.sgst).toLocaleString("en-IN")}
IGST: Rs.${Math.round(totals.igst).toLocaleString("en-IN")}
Total Orders: ${totals.orders}
Total Expenses: Rs.${Math.round(expTotal).toLocaleString("en-IN")}
Net Profit: Rs.${Math.round(grossProfit).toLocaleString("en-IN")}

Provide:
1. Key observations (2-3 points)
2. GST compliance check
3. One actionable recommendation

Be specific and practical. Return as JSON: {"observations":["...","..."],"compliance":"...","recommendation":"..."}`,
          maxTokens: 500,
        }),
      });
      const text = (data.result||"").replace(/```json|```/g,"").trim();
      setAiResult(JSON.parse(text));
      toast("AI analysis complete ✓","success");
    } catch(e) { toast("AI error: "+e.message,"error"); }
    setAiLoading(false);
  };

  const EXP_CATEGORIES = ["Packaging","Logistics","Marketing","Platform Fees","Salaries","Rent","Utilities","Inventory","Returns","Software","Other"];

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>💰 Finance & GST</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>GST reports, expenses, P&L and Tally export</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {/* Month/Year picker */}
          <select style={{ ...S.input, width:"auto" }} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
            {MONTHS.map((m,i)=><option key={i} value={i}>{m}</option>)}
          </select>
          <select style={{ ...S.input, width:"auto" }} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
            {[2023,2024,2025,2026].map(y=><option key={y}>{y}</option>)}
          </select>
          <select style={{ ...S.input, width:"auto" }} value={platFilter} onChange={e=>setPlatFilter(e.target.value)}>
            <option value="all">All Platforms</option>
            {PLATFORMS.map(p=><option key={p} value={p}>{PLT_NAMES[p]}</option>)}
          </select>
          <select style={{ ...S.input, width:"auto" }} value={gstRate} onChange={e=>setGstRate(parseInt(e.target.value))}>
            {GST_RATES.map(r=><option key={r} value={r}>GST {r}%</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t=>(
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ DASHBOARD ══ */}
      {tab === "dashboard" && (
        <div>
          {/* KPI strip */}
          <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Gross Revenue",    value:INR(totals.revenue),    color:"#2563eb", sub:`${totals.orders} orders` },
              { label:"Taxable Value",    value:INR(totals.taxable),    color:"#15803d", sub:"Excl. GST" },
              { label:"Output GST",       value:INR(totals.outputGST),  color:"#7e22ce", sub:"Collected from customers" },
              { label:"Net Profit (Est)", value:INR(grossProfit),       color:grossProfit>=0?"#15803d":"#dc2626", sub:"Revenue - Expenses" },
            ].map(k=>(
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", marginBottom:3 }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
                <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          <div className="two-col-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {/* GST Summary */}
            <div style={S.card}>
              <div style={S.hdr}><span>🧾 GST Summary — {MONTHS[month]} {year}</span></div>
              <div style={{ padding:14 }}>
                {[
                  { label:"Output GST collected",     value:totals.outputGST, color:"#dc2626" },
                  { label:"CGST (intra-state)",        value:totals.cgst,      color:"#7e22ce" },
                  { label:"SGST (intra-state)",        value:totals.sgst,      color:"#7e22ce" },
                  { label:"IGST (inter-state)",        value:totals.igst,      color:"#2563eb" },
                  { label:"Input GST / ITC (expenses)",value:expGSTTotal,      color:"#15803d" },
                  { label:"Net GST payable",           value:netGSTPayable,    color:netGSTPayable>=0?"#dc2626":"#15803d" },
                ].map(r=>(
                  <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f3f4f6" }}>
                    <span style={{ fontSize:12, color:"#6b7280" }}>{r.label}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:r.color }}>{INR(r.value)}</span>
                  </div>
                ))}
                <div style={{ marginTop:12 }}>
                  <button style={{ ...S.btn(), width:"100%", marginBottom:6 }} onClick={exportGSTR1}>⬇ Export GSTR-1 CSV</button>
                  <button style={{ ...S.btnOut, width:"100%", fontSize:11 }} onClick={exportTally}>📤 Export to Tally</button>
                </div>
              </div>
            </div>

            {/* Expense summary */}
            <div style={S.card}>
              <div style={S.hdr}>
                <span>💸 Expenses — {MONTHS[month]} {year}</span>
                <button style={S.btn()} onClick={()=>setShowExpForm(true)}>+ Add</button>
              </div>
              <div style={{ padding:14 }}>
                <div style={{ fontSize:24, fontWeight:700, color:"#dc2626", marginBottom:10 }}>{INR(expTotal)}</div>
                {Object.entries(expByCategory).slice(0,6).map(([cat,amt])=>(
                  <div key={cat} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"5px 0", borderBottom:"1px solid #f9fafb" }}>
                    <span style={{ color:"#6b7280" }}>{cat}</span>
                    <span style={{ fontWeight:600 }}>{INR(amt)}</span>
                  </div>
                ))}
                {expMonthly.length===0 && <div style={{ color:"#9ca3af", fontSize:12 }}>No expenses this month</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ GST REPORTS ══ */}
      {tab === "gst" && (
        <div>
          {/* Myntra data banner */}
          {myntraOrders.length > 0 && (
            <div style={{ background:"#fff0f5", border:"1px solid #fbb6ce", borderRadius:8, padding:"8px 14px", marginBottom:10, fontSize:12, color:"#b5003c", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span>
                📊 Including <strong>{myntraOrders.filter(o=>{ const d=new Date(o.created_at||0); return d.getMonth()===month&&d.getFullYear()===year; }).length}</strong> Myntra delivered orders for {MONTHS[month]} {year}
                &nbsp;· Seller price used for GST (not GMV)
              </span>
              <span style={{ fontSize:10, color:"#b5003c" }}>Total stored: {myntraOrders.length}</span>
            </div>
          )}
          {myntraOrders.length === 0 && (
            <div style={{ background:"#f9fafb", borderRadius:8, padding:"8px 14px", marginBottom:10, fontSize:11, color:"#9ca3af" }}>
              💡 Upload Myntra CSV in Channels → Myntra Reports to include Myntra orders here
            </div>
          )}
          {/* Summary bar */}
          <div className="six-col-grid" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, marginBottom:14 }}>
            {[
              { label:"Orders",   value:totals.orders },
              { label:"Revenue",  value:INR(totals.revenue) },
              { label:"Taxable",  value:INR(totals.taxable) },
              { label:"CGST",     value:INR(totals.cgst) },
              { label:"SGST",     value:INR(totals.sgst) },
              { label:"IGST",     value:INR(totals.igst) },
            ].map(k=>(
              <div key={k.label} style={{ background:"#fff", borderRadius:8, border:"1px solid #e5e7eb", padding:"10px 12px", textAlign:"center" }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:15, fontWeight:700, color:"#1A3A5C", marginTop:2 }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <button style={S.btn("#15803d")} onClick={exportGSTR1}>⬇ Export GSTR-1</button>
            <button style={S.btnOut} onClick={exportTally}>📤 Export Tally XML</button>
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}>
                  <span>📋 GSTR-1 — {MONTHS[month]} {year} ({gstData.length} orders)</span>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span style={{ fontSize:10, color:"#9ca3af" }}>
                      Shopify: {gstData.filter(r=>r.platform==="shopify").length} ·
                      Myntra: {gstData.filter(r=>r.platform==="myntra").length} ·
                      Others: {gstData.filter(r=>!["shopify","myntra"].includes(r.platform)).length}
                    </span>
                  </div>
                </div>
            <div style={{ overflowX:"auto", maxHeight:500, overflowY:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead style={{ position:"sticky", top:0 }}>
                  <tr>
                    {["Order ID","Date","Customer","Platform","Invoice Amt","Taxable","CGST","SGST","IGST","Total GST","State"].map(h=>(
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gstData.length===0 ? (
                    <tr><td colSpan={11} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>No orders for {MONTHS[month]} {year}</td></tr>
                  ) : gstData.map((r,i)=>(
                    <tr key={i} style={{ background:i%2===0?"#fff":"#fafafa" }}>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{r.orderId}</td>
                      <td style={S.td}>{r.date}</td>
                      <td style={{ ...S.td, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.customer}</td>
                      <td style={S.td}><span style={{ fontSize:10, fontWeight:600, color:r.platform==="amazon"?"#c45000":r.platform==="flipkart"?"#1b57c0":"#5a8a00" }}>{PLT_NAMES[r.platform]||r.platform}</span></td>
                      <td style={{ ...S.td, textAlign:"right" }}>{INR(r.sp)}</td>
                      <td style={{ ...S.td, textAlign:"right" }}>{INR(r.taxableValue)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:r.cgst>0?"#7e22ce":"#9ca3af" }}>{INR(r.cgst)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:r.sgst>0?"#7e22ce":"#9ca3af" }}>{INR(r.sgst)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:r.igst>0?"#2563eb":"#9ca3af" }}>{INR(r.igst)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:600, color:"#dc2626" }}>{INR(r.outputGST)}</td>
                      <td style={S.td}><span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#f3f4f6", fontWeight:600 }}>{r.stCode}</span></td>
                    </tr>
                  ))}
                </tbody>
                {gstData.length>0 && (
                  <tfoot>
                    <tr style={{ background:"#f9fafb", fontWeight:700 }}>
                      <td colSpan={4} style={{ ...S.td, fontWeight:700 }}>TOTAL ({gstData.length} orders)</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700 }}>{INR(totals.revenue)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700 }}>{INR(totals.taxable)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#7e22ce" }}>{INR(totals.cgst)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#7e22ce" }}>{INR(totals.sgst)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#2563eb" }}>{INR(totals.igst)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#dc2626" }}>{INR(totals.outputGST)}</td>
                      <td style={S.td}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ INVOICES ══ */}
      {tab === "invoices" && (
        <div>
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>📄 Invoice History</span><span style={{ fontSize:11, color:"#9ca3af" }}>{invoices.length} invoices</span></div>
            {invoices.length===0 ? (
              <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📄</div>
                <div>No invoices yet — generate from Orders page</div>
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{["Invoice #","Date","Customer","Amount","GST","Status"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {invoices.map((inv,i)=>(
                      <tr key={i} style={{ background:i%2?"#fafafa":"#fff" }}>
                        <td style={{ ...S.td, fontFamily:"monospace", color:"#F47920" }}>{inv.invoiceNo||"—"}</td>
                        <td style={S.td}>{inv.date||"—"}</td>
                        <td style={S.td}>{inv.customerName||"—"}</td>
                        <td style={{ ...S.td, fontWeight:600 }}>{INR(inv.total||0)}</td>
                        <td style={{ ...S.td, color:"#7e22ce" }}>{INR(inv.gst||0)}</td>
                        <td style={S.td}><span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:"#f0fdf4", color:"#15803d", fontWeight:700 }}>PAID</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ EXPENSES ══ */}
      {tab === "expenses" && (
        <div>
          {/* Add expense form */}
          {showExpForm && (
            <div style={{ ...S.card, padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>➕ Add Expense</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                <div>
                  <label style={S.label}>Date</label>
                  <input type="date" style={{ ...S.input, width:"100%" }} value={expForm.date} onChange={e=>setExpForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Category</label>
                  <select style={{ ...S.input, width:"100%" }} value={expForm.category} onChange={e=>setExpForm(f=>({...f,category:e.target.value}))}>
                    <option value="">Select…</option>
                    {EXP_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Amount ₹</label>
                  <input type="number" style={{ ...S.input, width:"100%" }} placeholder="0" value={expForm.amount} onChange={e=>setExpForm(f=>({...f,amount:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Description</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="e.g. Blue Dart courier charges" value={expForm.desc} onChange={e=>setExpForm(f=>({...f,desc:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>GST on expense %</label>
                  <select style={{ ...S.input, width:"100%" }} value={expForm.gstPct} onChange={e=>setExpForm(f=>({...f,gstPct:e.target.value}))}>
                    {GST_RATES.map(r=><option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btn()} onClick={addExpense}>Save Expense</button>
                <button style={S.btnOut} onClick={()=>setShowExpForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Expense KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"This Month", value:INR(expTotal),    color:"#dc2626" },
              { label:"Input GST",  value:INR(expGSTTotal), color:"#15803d", sub:"Claimable as ITC" },
              { label:"Categories", value:Object.keys(expByCategory).length, color:"#2563eb" },
              { label:"Entries",    value:expMonthly.length, color:"#6b7280" },
            ].map(k=>(
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
                {k.sub && <div style={{ fontSize:10, color:"#9ca3af" }}>{k.sub}</div>}
              </div>
            ))}
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:10 }}>
            <button style={S.btn()} onClick={()=>setShowExpForm(true)}>+ Add Expense</button>
            <button style={S.btnOut} onClick={()=>{
              const csv = ["Date,Category,Description,Amount,GST%,GST Amount",...expMonthly.map(e=>`${e.date},${e.category},"${e.desc}",${e.amount},${e.gstPct}%,${(parseFloat(e.amount||0)*parseFloat(e.gstPct||0)/100).toFixed(2)}`)].join("\n");
              const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download=`Expenses_${MONTHS[month]}_${year}.csv`; a.click();
              toast("Expenses exported ✓","success");
            }}>⬇ Export CSV</button>
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>💸 Expenses — {MONTHS[month]} {year}</span></div>
            {expMonthly.length===0 ? (
              <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>No expenses for {MONTHS[month]} {year}</div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Date","Category","Description","Amount","GST%","GST Amt","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {expMonthly.map((e,i)=>(
                    editingExp?.id===e.id ? (
                      /* ── Inline Edit Row ── */
                      <tr key={e.id} style={{ background:"#fff8f2" }}>
                        <td style={S.td}>
                          <input type="date" style={{ ...S.input, width:110, fontSize:11, padding:"4px 6px" }}
                            value={editingExp.date} onChange={ev=>setEditingExp(x=>({...x,date:ev.target.value}))}/>
                        </td>
                        <td style={S.td}>
                          <select style={{ ...S.input, width:120, fontSize:11, padding:"4px 6px" }}
                            value={editingExp.category} onChange={ev=>setEditingExp(x=>({...x,category:ev.target.value}))}>
                            {EXP_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width:"100%", fontSize:11, padding:"4px 6px" }}
                            placeholder="Description" value={editingExp.desc||""} onChange={ev=>setEditingExp(x=>({...x,desc:ev.target.value}))}/>
                        </td>
                        <td style={S.td}>
                          <input type="number" style={{ ...S.input, width:80, fontSize:11, padding:"4px 6px" }}
                            value={editingExp.amount} onChange={ev=>setEditingExp(x=>({...x,amount:ev.target.value}))}/>
                        </td>
                        <td style={S.td}>
                          <select style={{ ...S.input, width:65, fontSize:11, padding:"4px 6px" }}
                            value={editingExp.gstPct} onChange={ev=>setEditingExp(x=>({...x,gstPct:ev.target.value}))}>
                            {GST_RATES.map(r=><option key={r} value={r}>{r}%</option>)}
                          </select>
                        </td>
                        <td style={{ ...S.td, color:"#15803d" }}>{INR(parseFloat(editingExp.amount||0)*parseFloat(editingExp.gstPct||0)/100)}</td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:5 }}>
                            <button style={{ padding:"3px 8px", borderRadius:5, fontSize:10, cursor:"pointer", background:"#15803d", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                              onClick={saveEditExpense}>Save</button>
                            <button style={{ padding:"3px 8px", borderRadius:5, fontSize:10, cursor:"pointer", background:"#f3f4f6", color:"#374151", border:"none", fontFamily:"inherit" }}
                              onClick={cancelEditExpense}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      /* ── Normal Row ── */
                      <tr key={e.id} style={{ background:i%2?"#fafafa":"#fff" }}
                        onMouseEnter={ev=>ev.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={ev=>ev.currentTarget.style.background=i%2?"#fafafa":"#fff"}>
                        <td style={S.td}>{e.date}</td>
                        <td style={S.td}><span style={{ background:"#f3f4f6", padding:"2px 8px", borderRadius:6, fontSize:11 }}>{e.category}</span></td>
                        <td style={{ ...S.td, color:"#6b7280" }}>{e.desc||"—"}</td>
                        <td style={{ ...S.td, fontWeight:600 }}>{INR(e.amount)}</td>
                        <td style={S.td}>{e.gstPct}%</td>
                        <td style={{ ...S.td, color:"#15803d" }}>{INR(parseFloat(e.amount||0)*parseFloat(e.gstPct||0)/100)}</td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:5 }}>
                            <button style={{ padding:"3px 8px", borderRadius:5, fontSize:10, cursor:"pointer", background:"#eff6ff", color:"#2563eb", border:"none", fontFamily:"inherit", fontWeight:600 }}
                              onClick={()=>startEditExpense(e)}>Edit</button>
                            <button style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:14 }}
                              onClick={()=>deleteExpense(e.id)}>×</button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                  <tr style={{ background:"#f9fafb", fontWeight:700 }}>
                    <td colSpan={3} style={{ ...S.td, fontWeight:700 }}>TOTAL</td>
                    <td style={{ ...S.td, fontWeight:700, color:"#dc2626" }}>{INR(expTotal)}</td>
                    <td style={S.td}></td>
                    <td style={{ ...S.td, fontWeight:700, color:"#15803d" }}>{INR(expGSTTotal)}</td>
                    <td style={S.td}></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ══ P&L ══ */}
      {tab === "pl" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* P&L Statement */}
            <div style={S.card}>
              <div style={S.hdr}><span>📈 P&L Statement — {MONTHS[month]} {year}</span></div>
              <div style={{ padding:14 }}>
                {[
                  { label:"Gross Revenue (incl. GST)", value:totals.revenue, color:"#15803d", bold:false },
                  { label:"Less: Output GST",           value:-totals.outputGST, color:"#dc2626", bold:false },
                  { label:"Net Revenue (excl. GST)",    value:totals.taxable,  color:"#15803d", bold:true },
                  { label:"", value:null },
                  { label:"Total Expenses",             value:-expTotal,       color:"#dc2626", bold:false },
                  { label:"Less: Input GST (ITC)",      value:expGSTTotal,     color:"#15803d", bold:false },
                  { label:"Net Expenses",               value:-(expTotal-expGSTTotal), color:"#dc2626", bold:true },
                  { label:"", value:null },
                  { label:"Net Profit / Loss",          value:grossProfit+expGSTTotal, color:(grossProfit+expGSTTotal)>=0?"#15803d":"#dc2626", bold:true },
                  { label:"Net Profit Margin",          value:totals.taxable>0?PCT(((grossProfit+expGSTTotal)/totals.taxable)*100):"-", color:(grossProfit+expGSTTotal)>=0?"#15803d":"#dc2626", bold:false, isText:true },
                ].map((r,i)=>{
                  if (!r.label && r.value===null) return <div key={i} style={{ height:8 }}/>;
                  return (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:r.bold?"10px 0":"6px 0", borderBottom:r.bold?"2px solid #e5e7eb":"1px solid #f9fafb", marginTop:r.bold?4:0 }}>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:400, color:r.bold?"#1A3A5C":"#6b7280" }}>{r.label}</span>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:500, color:r.color }}>{r.isText?r.value:INR(Math.abs(r.value||0))}{r.value<0&&!r.isText?" (Dr)":""}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GST Payable */}
            <div>
              <div style={S.card}>
                <div style={S.hdr}><span>🏛️ GST Payable — {MONTHS[month]} {year}</span></div>
                <div style={{ padding:14 }}>
                  {[
                    { label:"Output GST collected",    value:totals.outputGST,    color:"#dc2626" },
                    { label:"CGST",                    value:totals.cgst,          color:"#7e22ce", sub:true },
                    { label:"SGST",                    value:totals.sgst,          color:"#7e22ce", sub:true },
                    { label:"IGST",                    value:totals.igst,          color:"#2563eb", sub:true },
                    { label:"Input GST (ITC)",         value:expGSTTotal,          color:"#15803d" },
                    { label:"Net GST Payable to Govt", value:netGSTPayable,        color:netGSTPayable>=0?"#dc2626":"#15803d", bold:true },
                  ].map((r,i)=>(
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", paddingLeft:r.sub?16:0, borderBottom:r.bold?"2px solid #e5e7eb":"1px solid #f9fafb", marginTop:r.bold?4:0 }}>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:400, color:r.bold?"#1A3A5C":r.sub?"#9ca3af":"#6b7280" }}>{r.label}</span>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:500, color:r.color }}>{INR(r.value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Platform revenue breakdown */}
              <div style={S.card}>
                <div style={S.hdr}><span>📊 Revenue by Platform</span></div>
                <div style={{ padding:14 }}>
                  {PLATFORMS.map(plat=>{
                    const platOrders = filteredOrders.filter(o=>(o._platform||"shopify")===plat);
                    const platRev    = platOrders.reduce((a,o)=>a+parseFloat(o.total_price||o.OrderTotal?.Amount||0),0);
                    const pct        = totals.revenue>0?Math.round(platRev/totals.revenue*100):0;
                    const colors     = { shopify:"#5a8a00", amazon:"#c45000", flipkart:"#1b57c0", myntra:"#b5003c", meesho:"#9f2089" };
                    return (
                      <div key={plat} style={{ marginBottom:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
                          <span style={{ fontWeight:600, color:colors[plat] }}>{PLT_NAMES[plat]}</span>
                          <span style={{ color:"#6b7280" }}>{INR(platRev)} ({pct}%)</span>
                        </div>
                        <div style={{ height:5, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:5, background:colors[plat], width:pct+"%", borderRadius:3 }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ TALLY EXPORT ══ */}
      {tab === "tally" && (
        <div>
          <div style={{ ...S.card, padding:16 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>📤 Tally Export</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:16 }}>Export your sales data in Tally-compatible XML format. Import this file into Tally ERP 9 or TallyPrime.</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
              <div style={{ background:"#f9fafb", borderRadius:8, padding:14 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>📊 Sales Vouchers</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{gstData.length} orders · {MONTHS[month]} {year}</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:12 }}>Revenue: {INR(totals.revenue)} · GST: {INR(totals.outputGST)}</div>
                <button style={{ ...S.btn(), width:"100%" }} onClick={exportTally}>⬇ Download XML</button>
              </div>
              <div style={{ background:"#f9fafb", borderRadius:8, padding:14 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>💸 Expense Vouchers</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{expMonthly.length} expenses · {MONTHS[month]} {year}</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:12 }}>Total: {INR(expTotal)} · ITC: {INR(expGSTTotal)}</div>
                <button style={{ ...S.btn("#15803d"), width:"100%" }} onClick={()=>{
                  const xml = `<?xml version="1.0"?><ENVELOPE><BODY><IMPORTDATA><REQUESTDATA>
${expMonthly.map(e=>`<TALLYMESSAGE><VOUCHER VCHTYPE="Purchase"><DATE>${e.date.replace(/-/g,"")}</DATE><NARRATION>${e.category} - ${e.desc}</NARRATION><AMOUNT>${e.amount}</AMOUNT></VOUCHER></TALLYMESSAGE>`).join("\n")}
</REQUESTDATA></IMPORTDATA></BODY></ENVELOPE>`;
                  const a=document.createElement("a"); a.href="data:text/xml;charset=utf-8,"+encodeURIComponent(xml); a.download=`TallyExpenses_${MONTHS[month]}_${year}.xml`; a.click();
                  toast("Expense XML exported ✓","success");
                }}>⬇ Download XML</button>
              </div>
            </div>
            <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#1e40af" }}>
              <strong>How to import:</strong> Open Tally → Gateway of Tally → Import → Vouchers → Select the XML file → Done
            </div>
          </div>
        </div>
      )}

      {/* ══ AI ANALYSIS ══ */}
      {tab === "ai" && (
        <div>
          <div style={{ ...S.card, padding:16, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>🤖 AI Financial Analysis</div>
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>AI analyzes your GST, revenue and expenses to give actionable insights</div>
              </div>
              <button style={{ ...S.btn("#6366f1"), opacity:aiLoading?0.7:1 }} onClick={runAI} disabled={aiLoading}>
                {aiLoading?"🤖 Analyzing…":"🤖 Run Analysis"}
              </button>
            </div>
            {/* Context */}
            <div style={{ background:"#f9fafb", borderRadius:8, padding:12, fontSize:12, color:"#6b7280" }}>
              Analyzing: <strong>{MONTHS[month]} {year}</strong> · Revenue: <strong>{INR(totals.revenue)}</strong> · GST: <strong>{INR(totals.outputGST)}</strong> · Expenses: <strong>{INR(expTotal)}</strong>
            </div>
          </div>

          {aiResult && (
            <div>
              <div style={S.card}>
                <div style={S.hdr}><span>💡 Observations</span></div>
                <div style={{ padding:14 }}>
                  {(aiResult.observations||[]).map((obs,i)=>(
                    <div key={i} style={{ display:"flex", gap:10, marginBottom:10, padding:"10px 12px", background:"#f9fafb", borderRadius:8 }}>
                      <span style={{ color:"#F47920", fontWeight:700, flexShrink:0 }}>{i+1}.</span>
                      <span style={{ fontSize:13, color:"#374151" }}>{obs}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div style={S.card}>
                  <div style={S.hdr}><span>✅ GST Compliance</span></div>
                  <div style={{ padding:14, fontSize:13, color:"#374151", lineHeight:1.6 }}>{aiResult.compliance}</div>
                </div>
                <div style={S.card}>
                  <div style={S.hdr}><span>🎯 Recommendation</span></div>
                  <div style={{ padding:14, fontSize:13, color:"#374151", lineHeight:1.6 }}>{aiResult.recommendation}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
